import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedNumber } from 'react-intl';
import { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid-premium';

import { type DetailsPageContentProps } from 'app/main/components/page-layouts/details/DetailsPage';
import { Icon, showDeleteDialog, useSettings } from 'app/main/components';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';
import { showItemMerchPointsEditLineDialog } from '../../components/ItemMerchPointsEditLineDialog';

type ItemsMerchPointsTabProps = Pick<DetailsPageContentProps, 'model'>;

export default function ItemsMerchPointsTab({ model }: ItemsMerchPointsTabProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	function openEditLineDialog(row) {
		dispatch(showItemMerchPointsEditLineDialog({ baseModel: row, updateRefresh }));
	}

	function deleteLine({ id, title }) {
		dispatch(
			showDeleteDialog({
				module: 'items_merchpoints',
				id,
				title,
				updateRefresh,
				hardDelete: true,
				replace: false
			})
		);
	}

	const columns: GridColDef[] = [
		{
			field: 'itmp_d_from',
			headerName: t('DETAILS_ITEMS_MERCHPOINTS_FIELD_DATE_FROM'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'itmp_d_to',
			headerName: t('DETAILS_ITEMS_MERCHPOINTS_FIELD_DATE_TO'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'pric_title',
			headerName: t('DETAILS_ITEMS_MERCHPOINTS_FIELD_PRICING_GROUP'),
			minWidth: 200,
			flex: 1,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			hideable: false
		},
		{
			field: 'itmp_points_price',
			type: 'number',
			headerName: t('DETAILS_ITEMS_MERCHPOINTS_FIELD_POINTS'),
			minWidth: 100,
			renderCell: ({ value }) => <FormattedNumber value={-value} signDisplay="exceptZero" />,
			hideable: false
		},
		{
			field: 'actions',
			type: 'actions',
			width: 100,
			getActions: ({ row }) => [
				<GridActionsCellItem
					label={t('VIEW_ITEM_BUTTON_EDIT')}
					icon={<Icon icon="edit" />}
					onClick={() => openEditLineDialog(row)}
				/>,
				<GridActionsCellItem
					label={t('VIEW_ITEM_BUTTON_DELETE')}
					icon={<Icon icon="delete" />}
					onClick={() => deleteLine({ id: row.itmp_uid, title: row.itmp_title })}
				/>
			]
		}
	];

	return (
		<ViewTable
			module="items_merchpoints"
			columns={columns}
			defaultFilters={[
				{
					filt_field: 'itmp_item_uid',
					filt_clause: 'equals',
					filt_value: model.item_uid
				}
			]}
			refresh={refresh}
			updateRefresh={updateRefresh}
		/>
	);
}
